import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
    FiArrowRight,
    FiBell,
    FiCheck,
    FiCheckCircle,
    FiCreditCard,
    FiDownload,
    FiHeadphones,
    FiImage,
    FiLock,
    FiPackage,
    FiShield,
    FiSmartphone,
    FiUser,
    FiZap
} from 'react-icons/fi'
import { FaApple } from 'react-icons/fa'

import Headers from '../components/Headers'
import Footer from '../components/Footer'
import api from '../api/api'

// ─────────────────────────────────────────────────────────────────────────────
// /mobile-app — Itemzo APK yükləmə səhifəsi (sıfırdan yenidən qurulub).
//
//  • Məzmun admin-idarəlidir: GET /api/mobile/app-config?platform=android
//    (MobileAppConfig — downloadPage* sahələri). Boş sahələr üçün aşağıdakı
//    DEFAULTS istifadə olunur.
//  • Hero sağ paneli: admin şəkli varsa göstərilir; yoxdursa süni telefon
//    mockup-u QOYULMUR — panel premium bənövşəyi "glow" boşluğu kimi qalır.
//  • Yükləmə düyməsi prioriteti: apkUrl → playStoreUrl → deaktiv.
//  • Saxta statistika (reytinq, yükləmə sayı, fayl ölçüsü) YOXDUR — yalnız
//    real konfiqurasiya dəyərləri göstərilir.
//
//  ⚠️ Tailwind breakpoint-ləri bu layihədə MAX-WIDTH-dir (desktop-first):
//     base = desktop, `md-lg:` = ≤991px, `md:` = ≤768px, `sm:` = ≤576px.
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULTS = {
    badge: 'Yeni · ItemZo Mobil Tətbiq',
    title: 'ItemZo artıq cibində.\nAPK-nı yüklə, alış-verişə başla.',
    subtitle:
        'Oyun hesabları, gift kartlar, VPN və rəqəmsal məhsullar — hamısı bir tətbiqdə. Sürətli, təhlükəsiz və rahat istifadə ilə.',
    primaryButtonText: 'APK yüklə',
    secondaryButtonText: 'iOS üçün tezliklə',
    featureTitle: 'Alış-veriş üçün nə lazımdırsa — hamısı içində.',
    featureSubtitle: 'ItemZo mobil tətbiqi rəqəmsal məhsul almağı ən rahat yolla təqdim edir.',
    installTitle: '3 addımda hazırsan.',
    installSubtitle: 'Yüklə, icazə ver, daxil ol — vəssalam.',
    finalCtaTitle: 'Hələ gözləyirsən?',
    finalCtaSubtitle: 'APK-nı indi yüklə — 1 dəqiqəyə hazır.'
}

const TRUST_ITEMS = [
    { Icon: FiLock, tone: 'bg-emerald-50 text-emerald-600', text: 'Təhlükəsiz ödəniş' },
    { Icon: FiZap, tone: 'bg-amber-50 text-amber-600', text: 'Sürətli digital çatdırılma' },
    { Icon: FiHeadphones, tone: 'bg-sky-50 text-sky-600', text: '24/7 dəstək' },
    { Icon: FiCheckCircle, tone: 'bg-brand-50 text-brand-600', text: 'Rəsmi tətbiq' }
]

const FEATURES = [
    { Icon: FiCreditCard, title: 'Təhlükəsiz ödəniş', desc: 'Bütün ödənişlər qorunan infrastruktur üzərindən keçir.' },
    { Icon: FiZap, title: 'Anında çatdırılma', desc: 'Rəqəmsal məhsullar sifariş anında hesabına düşür.' },
    { Icon: FiHeadphones, title: '24/7 dəstək', desc: 'Sualın olanda dəstək komandası həmişə yanındadır.' },
    { Icon: FiBell, title: 'Bildirişlər', desc: 'Sifariş və kampaniya xəbərlərini ilk sən al.' },
    { Icon: FiPackage, title: 'Sifariş izləmə', desc: 'Sifarişinin hər addımını canlı izlə.' },
    { Icon: FiUser, title: 'Balans və hesab idarəsi', desc: 'Balansını artır, hesabını tək yerdən idarə et.' }
]

const INSTALL_STEPS = [
    { num: '01', title: 'APK-nı yüklə', desc: 'Yuxarıdakı düyməyə vur, .apk faylı telefonuna endirilsin.' },
    { num: '02', title: 'Quraşdırmağa icazə ver', desc: 'Telefon parametrlərindən “Naməlum mənbələr”ə bir dəfəlik icazə ver.' },
    { num: '03', title: 'Aç və daxil ol', desc: 'ItemZo hesabınla daxil ol və alış-verişə başla.' }
]

const SCREEN_LABELS = [
    { label: 'Ana səhifə', desc: 'Sənin üçün seçilmiş məhsullar' },
    { label: 'Kateqoriyalar', desc: 'Fiziki və rəqəmsal, hər şey burada' },
    { label: 'Sifariş', desc: 'Bir toxunuşla ödəniş' }
]

const MobileAppDownload = () => {
    const [config, setConfig] = useState(null)
    const [loadFailed, setLoadFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        api.get('/mobile/app-download', { params: { platform: 'android' } })
            .then(({ data }) => {
                if (!cancelled) setConfig(data?.data || {})
            })
            .catch(() => {
                // Konfiqurasiya gəlməsə səhifə default mətnlərlə işləyir.
                if (!cancelled) {
                    setLoadFailed(true)
                    setConfig({})
                }
            })
        return () => {
            cancelled = true
        }
    }, [])

    const c = config || {}

    // Mətnlər: admin dəyəri → default.
    const badge = c.badge || DEFAULTS.badge
    const title = c.heroTitle || DEFAULTS.title
    const subtitle = c.heroSubtitle || DEFAULTS.subtitle
    const primaryText = c.primaryButtonText || DEFAULTS.primaryButtonText
    const secondaryText = c.secondaryButtonText || DEFAULTS.secondaryButtonText
    const heroImageUrl = c.heroImageUrl || ''
    const latestVersion = c.latestVersion || ''

    // Yükləmə linki prioriteti: downloadUrl (V2 gated) → apkUrl → playStoreUrl → deaktiv.
    const downloadHref = c.downloadUrl || ''
    const downloadDisabled = !downloadHref

    // Ekran şəkilləri — yalnız unikal, boş olmayan URL-lər (eyni şəkil təkrarlanmır).
    const screenImages = useMemo(() => {
        const urls = Array.isArray(c.screenshots) ? c.screenshots : []
        const seen = new Set()
        return urls.map((url) => {
            const clean = (url || '').trim()
            if (!clean || seen.has(clean)) return ''
            seen.add(clean)
            return clean
        })
    }, [c.screenshots])

    const pageActive = true

    return (
        <div className='min-h-screen bg-white text-slate-700 antialiased'>
            <Helmet>
                <title>ItemZo Mobil Tətbiq — APK yüklə</title>
                <meta
                    name='description'
                    content='ItemZo mobil tətbiqi ilə oyun hesabları, gift kartlar, VPN və rəqəmsal məhsullar cibində. APK-nı yüklə, alış-verişə başla.'
                />
            </Helmet>
            <Headers />

            {config === null ? (
                <main className='flex min-h-[55vh] items-center justify-center px-5 text-center'>
                    <div>
                        <div className='mx-auto h-10 w-10 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600' />
                        <p className='mt-4 text-sm font-semibold text-slate-500'>Mobil tətbiq məlumatı yüklənir...</p>
                    </div>
                </main>
            ) : pageActive ? (
                <main>
                    {loadFailed && (
                        <p className='bg-amber-50 px-5 py-3 text-center text-sm font-semibold text-amber-700'>
                            Yenilənmiş məlumat hazırda alınmadı. Bir az sonra yenidən yoxlayın.
                        </p>
                    )}
                    <Hero
                        badge={badge}
                        title={title}
                        subtitle={subtitle}
                        primaryText={primaryText}
                        secondaryText={secondaryText}
                        heroImageUrl={heroImageUrl}
                        latestVersion={latestVersion}
                        latestBuildNumber={c.latestBuildNumber}
                        fileSize={c.fileSize}
                        releaseTitle={c.releaseTitle}
                        releaseNotes={c.releaseNotes}
                        qrCodeUrl={c.qrCodeUrl}
                        downloadHref={downloadHref}
                        downloadDisabled={downloadDisabled}
                    />
                    <TrustBar />
                    <FeaturesSection
                        title={c.featureTitle || DEFAULTS.featureTitle}
                        subtitle={c.featureSubtitle || DEFAULTS.featureSubtitle}
                    />
                    <ScreensSection images={screenImages} />
                    <InstallSection
                        title={c.installTitle || DEFAULTS.installTitle}
                        subtitle={c.installSubtitle || DEFAULTS.installSubtitle}
                    />
                    <FinalCta
                        title={c.finalCtaTitle || DEFAULTS.finalCtaTitle}
                        subtitle={c.finalCtaSubtitle || DEFAULTS.finalCtaSubtitle}
                        latestVersion={latestVersion}
                        downloadHref={downloadHref}
                        downloadDisabled={downloadDisabled}
                    />
                </main>
            ) : (
                <InactiveState />
            )}

            <Footer />
        </div>
    )
}

// ─── Yükləmə düyməsi (hero + final CTA eyni məntiqi paylaşır) ────────────────
const DownloadButton = ({ href, disabled, children, className = '' }) => {
    if (disabled) {
        return (
            <button
                type='button'
                disabled
                title='APK linki hələ əlavə olunmayıb'
                className={`inline-flex cursor-not-allowed items-center gap-2.5 rounded-2xl bg-slate-200 px-7 py-4 text-[15px] font-bold text-slate-400 ${className}`}
            >
                {children}
            </button>
        )
    }
    return (
        <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className={`group inline-flex items-center gap-2.5 rounded-2xl bg-brand-600 px-7 py-4 text-[15px] font-bold text-white shadow-lg shadow-brand-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-xl hover:shadow-brand-600/40 active:scale-[0.98] ${className}`}
        >
            {children}
        </a>
    )
}

// ─── HERO — desktopda 2 sütun: sol mətn / sağ vizual ────────────────────────
const Hero = ({
    badge,
    title,
    subtitle,
    primaryText,
    secondaryText,
    heroImageUrl,
    latestVersion,
    latestBuildNumber,
    fileSize,
    releaseTitle,
    releaseNotes,
    qrCodeUrl,
    downloadHref,
    downloadDisabled
}) => {
    // Başlıqdakı "\n" sətir sonlarını qoru (admin çox sətirli başlıq yaza bilir).
    const titleLines = String(title).split('\n')
    const formattedFileSize = fileSize ? `${(fileSize / (1024 * 1024)).toFixed(1)} MB` : null

    return (
        <section className='relative overflow-hidden bg-white'>
            {/* Yumşaq bənövşəyi işıqlar — dekorativ */}
            <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
                <div className='absolute -top-44 right-[-8%] h-[560px] w-[560px] rounded-full bg-brand-200/60 blur-[150px]' />
                <div className='absolute left-[-12%] top-1/3 h-[420px] w-[420px] rounded-full bg-fuchsia-200/40 blur-[140px]' />
            </div>

            <div className='relative mx-auto grid max-w-[1200px] grid-cols-[1.05fr_0.95fr] items-center gap-10 px-8 py-24 md-lg:grid-cols-1 md-lg:gap-14 md-lg:py-16 md:px-5'>
                {/* SOL — mətn bloku */}
                <div>
                    <div className='mb-7 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 px-4 py-1.5 text-xs font-bold text-brand-700 shadow-sm backdrop-blur'>
                        <span className='text-fuchsia-500'>✦</span>
                        {badge}
                    </div>

                    <h1 className='text-[56px] font-black leading-[1.05] tracking-tight text-slate-900 xl:text-5xl md:text-4xl'>
                        {titleLines.map((line, i) => (
                            <React.Fragment key={i}>
                                {i === 0 ? <GradientFirstLine line={line} /> : line}
                                {i < titleLines.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-slate-500 md:text-base'>{subtitle}</p>

                    <div className='mt-9 flex flex-wrap items-start gap-3'>
                        <DownloadButton href={downloadHref} disabled={downloadDisabled}>
                            <FiSmartphone className='h-[17px] w-[17px]' />
                            <span className='flex flex-col items-start leading-tight'>
                                <span className='text-[9.5px] font-bold tracking-[0.14em] opacity-70'>ANDROID ÜÇÜN</span>
                                <span>{primaryText}</span>
                            </span>
                            {!downloadDisabled && (
                                <FiArrowRight className='h-[13px] w-[13px] transition-transform duration-300 group-hover:translate-x-1' />
                            )}
                        </DownloadButton>

                        <button
                            type='button'
                            disabled
                            title='iOS versiyası hazırlanır'
                            className='inline-flex cursor-default items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-7 py-4 text-[15px] font-bold text-slate-400 shadow-sm'
                        >
                            <FaApple className='h-[17px] w-[17px]' />
                            <span className='flex flex-col items-start leading-tight'>
                                <span className='text-[9.5px] font-bold tracking-[0.14em] opacity-70'>iOS ÜÇÜN</span>
                                <span>{secondaryText}</span>
                            </span>
                        </button>

                        {qrCodeUrl && (
                            <div className='flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white p-2 pr-4 shadow-sm'>
                                <img src={qrCodeUrl} alt='QR Code' className='h-12 w-12 rounded-xl object-contain' />
                                <span className='text-xs font-bold text-slate-500 leading-tight'>
                                    Telefonla<br />skan edin
                                </span>
                            </div>
                        )}
                    </div>

                    {downloadDisabled && (
                        <p className='mt-4 text-sm font-semibold text-slate-400'>Mobil tətbiq tezliklə aktiv olacaq.</p>
                    )}

                    {/* Release Notes display */}
                    {releaseNotes && (
                        <div className='mt-6 max-w-xl rounded-2xl border border-brand-100 bg-brand-50/50 p-4'>
                            <p className='text-xs font-black uppercase tracking-wider text-brand-700'>
                                {releaseTitle || 'Yeniliklər (Release Notes)'}
                            </p>
                            <p className='mt-1 text-sm whitespace-pre-line text-slate-600 leading-relaxed'>
                                {releaseNotes}
                            </p>
                        </div>
                    )}

                    {/* Info sırası — yalnız real dəyərlər (saxta reytinq/ölçü YOXDUR) */}
                    <div className='mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold text-slate-500'>
                        {latestVersion && (
                            <span className='inline-flex items-center gap-1.5'>
                                <FiCheck className='h-3 w-3 text-brand-500' /> Versiya v{latestVersion}
                            </span>
                        )}
                        {latestBuildNumber && (
                            <span className='inline-flex items-center gap-1.5'>
                                <FiCheck className='h-3 w-3 text-brand-500' /> Build {latestBuildNumber}
                            </span>
                        )}
                        {formattedFileSize && (
                            <span className='inline-flex items-center gap-1.5'>
                                <FiCheck className='h-3 w-3 text-brand-500' /> Ölçü: {formattedFileSize}
                            </span>
                        )}
                        <span className='inline-flex items-center gap-1.5'>
                            <FiCheck className='h-3 w-3 text-brand-500' /> Android üçün APK
                        </span>
                        <span className='inline-flex items-center gap-1.5'>
                            <FiCheck className='h-3 w-3 text-brand-500' /> Rəsmi ItemZo
                        </span>
                    </div>
                </div>

                {/* SAĞ — admin-idarəli vizual sahə */}
                <div className='flex justify-end md-lg:justify-center'>
                    {heroImageUrl ? (
                        <div className='relative'>
                            <div
                                className='pointer-events-none absolute inset-0 scale-90 rounded-full bg-gradient-to-tr from-brand-400/50 via-fuchsia-400/40 to-brand-300/40 blur-[70px]'
                                aria-hidden='true'
                            />
                            <img
                                src={heroImageUrl}
                                alt='ItemZo mobil tətbiqi'
                                className='relative max-h-[560px] w-auto max-w-full rounded-3xl object-contain drop-shadow-[0_25px_45px_rgba(124,58,237,0.28)]'
                            />
                        </div>
                    ) : (
                        // Şəkil yoxdursa: saxta mockup QOYULMUR — premium boş glow paneli.
                        <div className='relative flex h-[480px] w-full max-w-[440px] items-center justify-center md-lg:h-[300px]'>
                            <div
                                className='absolute inset-8 rounded-full bg-gradient-to-tr from-brand-400/40 via-fuchsia-400/30 to-brand-300/30 blur-[80px]'
                                aria-hidden='true'
                            />
                            <div className='relative h-full w-full rounded-[2.5rem] border border-brand-100 bg-gradient-to-br from-brand-50/80 via-white to-fuchsia-50/60'>
                                <div
                                    className='absolute inset-0 rounded-[2.5rem]'
                                    aria-hidden='true'
                                    style={{
                                        backgroundImage:
                                            'radial-gradient(circle at 1px 1px, rgba(124,58,237,0.10) 1px, transparent 0)',
                                        backgroundSize: '22px 22px'
                                    }}
                                />
                                <div className='absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-brand-100 bg-white/90 px-4 py-2 text-xs font-bold text-brand-600 shadow-sm backdrop-blur'>
                                    <FiSmartphone className='h-3.5 w-3.5' />
                                    ItemZo Mobil
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

// Başlığın ilk sətrində brend sözünə qradiyent ver (yalnız vizual bəzək).
const GradientFirstLine = ({ line }) => {
    const idx = line.toLowerCase().indexOf('itemzo')
    if (idx === -1) return line
    const before = line.slice(0, idx)
    const word = line.slice(idx, idx + 6)
    const after = line.slice(idx + 6)
    return (
        <>
            {before}
            <span className='bg-gradient-to-r from-brand-600 to-fuchsia-500 bg-clip-text text-transparent'>{word}</span>
            {after}
        </>
    )
}

// ─── TRUST STRIP ─────────────────────────────────────────────────────────────
const TrustBar = () => (
    <section className='relative pb-6'>
        <div className='mx-auto max-w-[1200px] px-8 md:px-5'>
            <div className='grid grid-cols-4 gap-4 rounded-3xl border border-slate-100 bg-white px-6 py-5 shadow-lg shadow-slate-200/50 md-lg:grid-cols-2'>
                {TRUST_ITEMS.map(({ Icon, tone, text }) => (
                    <div key={text} className='flex items-center gap-3 md-lg:justify-center'>
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                            <Icon className='h-[15px] w-[15px]' />
                        </span>
                        <span className='text-sm font-bold text-slate-700'>{text}</span>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

// ─── FEATURES ────────────────────────────────────────────────────────────────
const FeaturesSection = ({ title, subtitle }) => (
    <section className='bg-white'>
        <div className='mx-auto max-w-[1200px] px-8 py-24 md:px-5 md:py-16'>
            <p className='mb-3 text-xs font-black uppercase tracking-[0.22em] text-brand-600'>Xüsusiyyətlər</p>
            <h2 className='max-w-2xl text-[40px] font-black leading-tight tracking-tight text-slate-900 md:text-3xl'>
                {title}
            </h2>
            <p className='mt-3 max-w-xl text-base text-slate-500'>{subtitle}</p>

            <div className='mt-10 grid grid-cols-3 gap-5 md-lg:grid-cols-2 md:grid-cols-1'>
                {FEATURES.map(({ Icon, title: fTitle, desc }) => (
                    <div
                        key={fTitle}
                        className='group rounded-2xl border border-slate-200/80 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:shadow-brand-600/10'
                    >
                        <div className='mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors duration-300 group-hover:bg-brand-600 group-hover:text-white'>
                            <Icon className='h-[17px] w-[17px]' />
                        </div>
                        <h4 className='mb-1.5 text-base font-black text-slate-900'>{fTitle}</h4>
                        <p className='text-sm leading-relaxed text-slate-500'>{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

// ─── SCREENS (açıq/ağ premium bölmə, admin-idarəli şəkillər) ────────────────
const ScreensSection = ({ images }) => {
    return (
        <section className='relative overflow-hidden bg-gradient-to-b from-white via-violet-50/30 to-white'>
            {/* Çox zərif bənövşəyi/fuşya işıqlar — açıq fona uyğun, gözə girmir */}
            <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
                <div className='absolute left-[-8%] top-[-25%] h-[460px] w-[460px] rounded-full bg-brand-200/30 blur-[160px]' />
                <div className='absolute bottom-[-30%] right-[-6%] h-[520px] w-[520px] rounded-full bg-fuchsia-200/25 blur-[170px]' />
            </div>

            <div className='relative mx-auto max-w-[1200px] px-8 py-24 md:px-5 md:py-16'>
                <div className='mb-12 flex items-end justify-between gap-5 md-lg:flex-col md-lg:items-start'>
                    <div>
                        <p className='mb-3 text-xs font-black uppercase tracking-[0.22em] text-brand-600'>Ekranlar</p>
                        <h2 className='text-[40px] font-black leading-tight tracking-tight text-slate-900 md:text-3xl'>
                            Gözəl dizayn. Rahat istifadə.
                        </h2>
                    </div>
                    <p className='max-w-sm text-right text-base text-slate-500 md-lg:text-left md:text-sm'>
                        Kateqoriyalar, mağazalar, səbət və dəstək — hamısı bir toxunuşda.
                    </p>
                </div>

                <div className='grid grid-cols-3 gap-6 md-lg:grid-cols-1'>
                    {SCREEN_LABELS.map((card, i) => (
                        <div
                            key={card.label}
                            className='flex flex-col items-center rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-brand-600/5 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-200 hover:shadow-xl hover:shadow-brand-600/10'
                        >
                            <div className='mb-5 flex h-72 w-full items-center justify-center overflow-hidden rounded-2xl'>
                                {images[i] ? (
                                    <img
                                        src={images[i]}
                                        alt={card.label}
                                        loading='lazy'
                                        className='max-h-72 w-auto max-w-full rounded-xl object-contain drop-shadow-[0_18px_30px_rgba(124,58,237,0.18)]'
                                    />
                                ) : (
                                    // Şəkil yoxdursa: açıq fonlu, zərif bənövşəyi dashed çərçivəli placeholder.
                                    <div className='relative flex h-full w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-200 bg-gradient-to-b from-brand-50/60 to-white'>
                                        <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-600'>
                                            <FiImage className='h-5 w-5' />
                                        </span>
                                        <span className='mt-4 text-xs font-bold uppercase tracking-widest text-brand-400'>
                                            Önizləmə tezliklə
                                        </span>
                                    </div>
                                )}
                            </div>
                            <h3 className='text-base font-black text-slate-900'>{card.label}</h3>
                            <p className='mt-1 text-center text-sm text-slate-500'>{card.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ─── INSTALL ─────────────────────────────────────────────────────────────────
const InstallSection = ({ title, subtitle }) => (
    <section className='bg-white'>
        <div className='mx-auto max-w-[1200px] px-8 py-24 md:px-5 md:py-16'>
            <p className='mb-3 text-xs font-black uppercase tracking-[0.22em] text-brand-600'>Necə qurulur</p>
            <h2 className='text-[40px] font-black leading-tight tracking-tight text-slate-900 md:text-3xl'>{title}</h2>
            {subtitle && <p className='mt-3 max-w-xl text-base text-slate-500'>{subtitle}</p>}

            <div className='mt-10 grid grid-cols-3 gap-5 md-lg:grid-cols-1'>
                {INSTALL_STEPS.map((step) => (
                    <div
                        key={step.num}
                        className='rounded-2xl border border-slate-200/80 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:shadow-brand-600/10'
                    >
                        <span className='select-none text-[56px] font-black leading-none tracking-tight text-brand-100'>
                            {step.num}
                        </span>
                        <h4 className='mb-2 mt-4 text-lg font-black text-slate-900'>{step.title}</h4>
                        <p className='text-sm leading-relaxed text-slate-500'>{step.desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
)

// ─── FINAL CTA ───────────────────────────────────────────────────────────────
const FinalCta = ({ title, subtitle, latestVersion, downloadHref, downloadDisabled }) => (
    <section className='pb-24 md:pb-16'>
        <div className='mx-auto max-w-[1200px] px-8 md:px-5'>
            <div className='relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-brand-600 via-brand-700 to-fuchsia-700 p-14 shadow-2xl shadow-brand-600/30 md:p-8'>
                <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
                    <div className='absolute -left-20 -top-28 h-96 w-96 rounded-full bg-white/25 blur-[110px]' />
                    <div className='absolute -bottom-32 -right-16 h-[420px] w-[420px] rounded-full bg-fuchsia-300/25 blur-[120px]' />
                </div>

                <div className='relative z-10 flex items-center justify-between gap-10 md-lg:flex-col md-lg:items-start'>
                    <div className='max-w-2xl'>
                        <h2 className='text-[44px] font-black leading-tight tracking-tight text-white md:text-3xl'>
                            {title}
                        </h2>
                        <p className='mt-3 text-xl font-semibold text-white/80 md:text-lg'>{subtitle}</p>
                    </div>

                    <div className='flex shrink-0 flex-col items-end md-lg:items-start'>
                        {downloadDisabled ? (
                            <button
                                type='button'
                                disabled
                                title='APK linki hələ əlavə olunmayıb'
                                className='inline-flex cursor-not-allowed items-center gap-3 rounded-2xl bg-white/20 px-9 py-4 text-base font-black text-white/50'
                            >
                                <FiDownload className='h-[15px] w-[15px]' />
                                <span>APK linki hələ əlavə olunmayıb</span>
                            </button>
                        ) : (
                            <a
                                href={downloadHref}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='group inline-flex items-center gap-3 rounded-2xl bg-white px-9 py-4 text-base font-black text-brand-700 shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-50 active:scale-[0.98]'
                            >
                                <FiDownload className='h-[15px] w-[15px]' />
                                <span>Pulsuz yüklə</span>
                                <FiArrowRight className='h-[13px] w-[13px] transition-transform duration-300 group-hover:translate-x-1' />
                            </a>
                        )}
                        <p className='mt-3 text-xs font-semibold text-white/70'>
                            <FiSmartphone className='mr-1.5 -mt-0.5 inline h-3 w-3' />
                            Android APK{latestVersion ? ` ? Versiya v${latestVersion}` : ''}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
)

// ─── Səhifə admin tərəfindən deaktiv edilibsə ───────────────────────────────
const InactiveState = () => (
    <main className='flex min-h-[60vh] items-center justify-center bg-white px-5'>
        <div className='text-center'>
            <span className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600'>
                <FiSmartphone className='h-6 w-6' />
            </span>
            <h1 className='text-2xl font-black text-slate-900'>Mobil tətbiq səhifəsi hazırlanır</h1>
            <p className='mt-2 text-slate-500'>Tezliklə burada olacaq.</p>
        </div>
    </main>
)

export default MobileAppDownload
